import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { fetchArtworks } from "@/lib/api";
import { Link } from "react-router-dom";

// Catalogue extracted from the Legacy PDF — works are oil & household paints
// on hardboard. Some are also represented in the active Chapter II Release
// (those will surface from /api/artworks).
const catalogue = [
  { slug: "universal-gaze",    title: "Universal Gaze",    image: "/artworks/universal-gaze.jpg",   dim: "122 × 61 cm" },
  { slug: "through-the-blue",  title: "Through The Blue",  image: "/artworks/through-the-blue.jpg", dim: "61 × 122 cm" },
  { slug: "reflected-grace",   title: "Reflected Grace",   image: "/artworks/reflected-grace.jpg",  dim: "122 × 61 cm" },
  { slug: "the-dome",          title: "The Dome",          image: "/artworks/the-dome.jpg",         dim: "122 × 122 cm" },
  { slug: "light-language",    title: "Light Language",    image: "/artworks/light-language.jpg",   dim: "122 × 122 cm" },
  { slug: "sign-from-above",   title: "Sign From Above",   image: "/artworks/sign-from-above.jpg",  dim: "122 × 92 cm" },
  { slug: "universal-race",    title: "Universal Race",    image: "/artworks/universal-race.jpg",   dim: "122 × 92 cm" },
  { slug: "six-wise-guys",     title: "Six Wise Guys",     image: "/artworks/six-wise-guys.jpg",    dim: "122 × 122 cm" },
];

export default function Work() {
  const [activeSlugs, setActiveSlugs] = useState(new Set());

  useEffect(() => {
    fetchArtworks()
      .then((arts) => setActiveSlugs(new Set(arts.map((a) => a.slug))))
      .catch(() => {});
  }, []);

  return (
    <Layout>
      <main data-testid="work-page" className="bg-[color:var(--bone)]">
        <PageHeader
          eyebrow="Catalogue"
          title="The Work."
          lede="A growing record of Nick's archive — paintings drawn from the East End flat where he worked alone for thirty years. Oil and household paints on hardboard."
          image="/release/tribal.jpg"
        />

        <section className="bone-section py-24 md:py-40">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <Reveal className="mb-16 md:mb-24 max-w-2xl">
              <p className="hairline opacity-60 mb-4">VIII works · expanding</p>
              <p className="font-serif italic text-2xl md:text-3xl opacity-80 leading-relaxed">
                The estate is documenting the archive in stages. New paintings
                are added as they are catalogued.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
              {catalogue.map((art, i) => {
                const active = activeSlugs.has(art.slug);
                return (
                  <Reveal key={art.slug} delay={(i % 4) * 90}>
                    <article
                      className="group"
                      data-testid={`catalogue-${art.slug}`}
                    >
                      <figure className="overflow-hidden bg-[color:var(--bone-deep)]">
                        <img
                          src={art.image}
                          alt={art.title}
                          loading="lazy"
                          className="w-full h-auto block transition-transform duration-[1800ms] ease-out group-hover:scale-[1.02]"
                        />
                      </figure>
                      <div className="mt-6 flex items-start justify-between">
                        <div>
                          <h3 className="font-serif italic text-2xl md:text-3xl leading-tight">
                            {art.title}
                          </h3>
                          <p className="hairline opacity-50 mt-2">
                            {art.dim} · oil &amp; household paints on hardboard
                          </p>
                        </div>
                        {active && (
                          <Link
                            to="/chapter-ii"
                            className="hairline text-[color:var(--oxblood)] opacity-90"
                            data-testid={`catalogue-active-${art.slug}`}
                          >
                            In release →
                          </Link>
                        )}
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>

            <Reveal className="mt-32 text-right">
              <Link
                to="/contact?topic=acquisition"
                className="link-quiet font-serif italic text-xl md:text-2xl"
                data-testid="work-enquire-link"
              >
                Enquire about an original →
              </Link>
            </Reveal>
          </div>
        </section>
      </main>
    </Layout>
  );
}
